import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Domain, Finding, FindingDetail, Me, RoadDetail } from "../../api/types";
import { Confidence, SeverityChip, StatusChip } from "../../components/Badges";
import { DataTable } from "../../components/DataTable";
import { EvidenceViewer } from "../../components/EvidenceViewer";
import { Panel, Segmented } from "../../components/Layout";
import { MapPanel } from "../../components/MapPanel";
import { EmptyState, QueryState, SkeletonBlock } from "../../components/States";
import { can } from "../../app/auth";
import { chainage, dateTime } from "../../lib/format";
import { COLORS, DOMAIN_COLOR } from "../../styles/themeColors";

type DomainFilter = "all" | Domain;
type StatusFilter = "all" | "verified" | "unreviewed" | "rejected";



export function useTrack(id: string) {
  return useQuery({ queryKey: ["road", id, "track"], queryFn: () => api<{ points: [number, number, number][] }>(`/api/roads/${id}/track`), staleTime: 5 * 60_000 });
}

export function FindingDetailPanel({ id, onClose, compact }: { id: string; onClose?: () => void; compact?: boolean }) {
  const q = useQuery({ queryKey: ["finding", id], queryFn: () => api<FindingDetail>(`/api/findings/${encodeURIComponent(id)}`) });
  return (
    <QueryState query={q} skeleton={<SkeletonBlock rows={6} />}>
      {(d) => {
        const f = d.finding;
        return (
          <div className="stack" style={{ gap: 16 }}>
            <div className="row">
              <h3 style={{ fontSize: 18 }}>{f.type_name}</h3>
              <StatusChip status={f.status} />
              <span className="spacer" />
              {onClose ? <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button> : null}
            </div>
            <EvidenceViewer evidence={d.evidence} label={`${f.type_name} at ${chainage(f.chainage_m)}`} dashed={f.status === "unreviewed"} />
            {f.status === "unreviewed" ? (
              <p className="notice warn" style={{ fontSize: 13 }}>AI finding not yet checked by an engineer. It is not counted in any total.</p>
            ) : null}
            <dl className="kv">
              <dt>Chainage</dt><dd className="mono">{chainage(f.chainage_m)}</dd>
              <dt>Category</dt><dd>{f.category}{f.type_code ? <span className="muted"> · {f.type_code}</span> : null}</dd>
              {f.severity ? <><dt>Severity</dt><dd><SeverityChip value={f.severity} /></dd></> : null}
              {f.treatment ? <><dt>Treatment</dt><dd>{f.treatment}</dd></> : null}
              {f.side_of_road ? <><dt>Side of road</dt><dd>{f.side_of_road}</dd></> : null}
              {f.confidence !== null ? <><dt>AI confidence</dt><dd><Confidence value={f.confidence} /></dd></> : null}
              <dt>Source</dt><dd>{f.source === "ai" ? "AI detection" : f.source === "manual" ? "Added by an engineer" : "Field observation"}</dd>
              {f.model ? <><dt>Found by</dt><dd>{f.model}</dd></> : null}
              {f.reviewed_by ? <><dt>Reviewed</dt><dd>{f.reviewed_by}, {dateTime(f.reviewed_at)}</dd></> : null}
              {f.lat && f.lon ? <><dt>Location</dt><dd className="mono">{f.lat.toFixed(6)}, {f.lon.toFixed(6)}</dd></> : null}
              {f.notes ? <><dt>Notes</dt><dd>{f.notes}</dd></> : null}
            </dl>
            {!compact && d.history.length ? (
              <div>
                <h4 style={{ fontSize: 14, marginBottom: 6 }}>Review history</h4>
                <ul className="muted" style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  {d.history.map((h, i) => <li key={i}>{dateTime(h.reviewed_at)} · {h.reviewed_by} · {h.action}{h.final_class ? ` → ${h.final_class}` : ""}{h.severity ? ` (${h.severity})` : ""}</li>)}
                </ul>
              </div>
            ) : null}
          </div>
        );
      }}
    </QueryState>
  );
}

function UnreviewedSwitch({ me }: { me: Me }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (v: boolean) => api("/api/auth/preferences", { method: "PATCH", json: { show_unreviewed: v } }),
    onSuccess: () => qc.invalidateQueries(),
  });
  return (
    <label className="switch">
      <input type="checkbox" checked={me.show_unreviewed} disabled={m.isPending} onChange={(e) => m.mutate(e.target.checked)} />
      Show unreviewed AI findings
    </label>
  );
}

export function FindingsTab({ road, me }: { road: RoadDetail; me: Me }) {
  const sid = road.survey.survey_uuid;
  const [params, setParams] = useSearchParams();
  const domain = (params.get("domain") || "all") as DomainFilter;
  const status = (params.get("status") || "all") as StatusFilter;
  const selected = params.get("f");
  const reviewer = can.review(me.role);
  const set = (k: string, v: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: k !== "f" });

  const fq = useQuery({
    queryKey: ["road", sid, "findings", domain, status === "rejected"],
    queryFn: () => api<{ items: Finding[] }>(`/api/roads/${sid}/findings?domain=${domain}${status === "rejected" ? "&include_rejected=true" : ""}`),
  });
  const track = useTrack(sid);
  const items = useMemo(() => (fq.data?.items ?? []).filter((f) => status === "all" || f.status === status), [fq.data, status]);

  return (
    <>
      <div className="row" style={{ gap: 12 }}>
        <Segmented<DomainFilter> label="Assessment" value={domain} onChange={(v) => set("domain", v === "all" ? null : v)} options={[
          { value: "all", label: "All" }, { value: "condition", label: "Road condition" }, { value: "assets", label: "Assets" }, { value: "safety", label: "Road safety" },
        ]} />
        <Segmented<StatusFilter> label="Status" value={status} onChange={(v) => set("status", v === "all" ? null : v)} options={[
          { value: "all", label: "All" }, { value: "verified", label: "Verified" }, { value: "unreviewed", label: "Unreviewed" },
          ...(reviewer ? [{ value: "rejected" as StatusFilter, label: "Rejected" }] : []),
        ]} />
        <span className="spacer" />
        {!reviewer ? <UnreviewedSwitch me={me} /> : null}
      </div>

      <div className="split">
        <div className="stack" style={{ gap: 16 }}>
          <Panel title="Map" id="fd-map">
            <MapPanel
              label="Survey track with findings"
              height={380}
              lines={track.data ? [{ id: "track", points: track.data.points.map((p) => [p[0], p[1]] as [number, number]), color: COLORS.navy, weight: 4 }] : []}
              pins={items.filter((f) => f.lat && f.lon).map((f) => ({
                id: f.id, lat: f.lat!, lon: f.lon!, color: DOMAIN_COLOR[f.domains[0]] ?? COLORS.accent,
                label: `${f.type_name} · ${chainage(f.chainage_m)}${f.status === "unreviewed" ? " · unreviewed" : ""}`, dashed: f.status === "unreviewed",
              }))}
              selectedId={selected}
              onSelectPin={(id) => set("f", id)}
              overlay={
                <div className="legend">
                  <span><i style={{ background: DOMAIN_COLOR.condition }} />Road condition</span>
                  <span><i style={{ background: DOMAIN_COLOR.assets }} />Asset</span>
                  <span><i style={{ background: DOMAIN_COLOR.safety }} />Safety</span>
                  <span>Hollow pin: unreviewed</span>
                </div>
              }
            />

          </Panel>
          <Panel title={`Findings (${items.length})`} id="fd-list" flush>
            {fq.isPending ? <div className="panel-body"><SkeletonBlock rows={6} /></div> : (
              <DataTable<Finding>
                rows={items}
                rowKey={(r) => r.id}
                selectedKey={selected}
                onRowClick={(r) => set("f", r.id)}
                rowClass={(r) => (r.status === "unreviewed" ? "unreviewed-row" : "")}
                maxHeight={420}
                initialSort={{ key: "ch", dir: "asc" }}
                caption="Findings on this road"
                empty={<EmptyState title="No findings here">{status !== "all" || domain !== "all" ? "Try another assessment or status." : reviewer ? "AI analysis may not have run on this survey yet." : "Nothing has been verified on this road yet."}</EmptyState>}
                columns={[
                  { key: "ch", label: "Chainage", sort: (r) => r.chainage_m, render: (r) => <span className="mono">{chainage(r.chainage_m)}</span> },
                  { key: "type", label: "Finding", sort: (r) => r.type_name, render: (r) => r.type_name },
                  { key: "sev", label: "Severity", sort: (r) => r.severity, render: (r) => <SeverityChip value={r.severity} /> },
                  { key: "conf", label: "Confidence", sort: (r) => r.confidence, render: (r) => <Confidence value={r.confidence} /> },
                  { key: "by", label: "Found by", sort: (r) => r.model ?? r.source, render: (r) => r.model ?? (r.source === "manual" ? "Engineer" : r.source === "field" ? "Field" : "AI") },
                  { key: "st", label: "Status", sort: (r) => r.status, render: (r) => <StatusChip status={r.status} /> },
                ]}
              />
            )}
          </Panel>
        </div>
        <Panel title="Evidence" id="fd-evidence">
          {selected ? <FindingDetailPanel id={selected} onClose={() => set("f", null)} /> : (
            <EmptyState title="Select a finding">Click a pin on the map or a row in the list to see its image and details.</EmptyState>
          )}
          {selected && reviewer && items.find((x) => x.id === selected)?.status === "unreviewed" ? (
            <Link className="btn btn-primary" style={{ marginTop: 16, width: "100%" }} to={`/review/${encodeURIComponent(selected)}?survey=${sid}`}>Review this finding</Link>
          ) : null}
        </Panel>
      </div>
    </>
  );
}
